import { Component } from '@theme/component-base';

/**
 * Swatches Item Component - Optimized for Performance
 * 
 * Handles color swatch interactions on product cards
 * - Uses event delegation for minimal listeners
 * - Preloads variant images on hover for instant switching
 * - CSS transitions instead of JS delays
 * - Minimal memory footprint
 */
class SwatchesItemComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Check if this is standalone (product page) or collection page
    this.isStandalone = this.hasAttribute('data-standalone');
    this.productCard = this.closest('[data-product-card]');
    
    // Set up "Show all" button functionality
    this.#setupShowAllButton();
    
    // For collection pages, we need the product card for image switching
    if (!this.isStandalone && !this.productCard) {
      return;
    }

    // Only set up image switching for collection page cards
    if (!this.isStandalone && this.productCard) {
      // Cache image elements (don't store full data, just references)
      this.productImages = this.productCard.querySelectorAll('[data-product-image]');
      if (this.productImages.length === 0) {
        return;
      }

      // Store ONLY original src/srcset as strings (minimal memory)
      this.originalSrc = this.productImages[0]?.src;
      this.originalSrcset = this.productImages[0]?.srcset;
      this.originalAlt = this.productImages[0]?.alt;

      // Track preloaded images for instant switching
      this.preloadedImages = new Set();

      this.#setupEventDelegation();
    }
  }

  /**
   * Single event listener using delegation - optimal for performance
   */
  #setupEventDelegation() {
    // FIRST: Block parent link clicks at the card level (capture phase)
    // This fires BEFORE any child handlers
    this.productCard.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-swatch-button]');
      if (swatch && this.contains(swatch)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Manually trigger our handler since we stopped propagation
        this.#handleSwatchClick(swatch);
        return false;
      }
    }, true);

    // Preload on hover for instant switching (UX optimization)
    this.addEventListener('mouseenter', (e) => {
      const button = e.target.closest('[data-swatch-button]');
      if (button && button.dataset.variantImage) {
        this.#preloadImage(button.dataset.variantImage);
      }
    }, true);
  }

  /**
   * Preload image for instant switching
   */
  #preloadImage(src) {
    if (this.preloadedImages.has(src)) return;
    
    const img = new Image();
    img.src = src;
    this.preloadedImages.add(src);
  }

  /**
   * Handles swatch click - instant image switch (already preloaded on hover)
   */
  #handleSwatchClick(button) {
    const variantImage = button.dataset.variantImage;
    const variantImageSrcset = button.dataset.variantImageSrcset;
    const variantImageAlt = button.dataset.variantImageAlt;
    const swatchValue = button.dataset.swatchValue;
    const hasSlider = this.closest('[data-product-card]')?.querySelector('product-media-slider');

    if (!variantImage) {
      return;
    }

    if (!hasSlider) {
      console.log('[Swatch] No slider found, updating image directly');
      // Old behavior: update image src for non-slider cards
      this.#updateImage(variantImage, variantImageSrcset, variantImageAlt);
    }

    const mediaId = button.dataset.mediaId;
    if (mediaId) {
      console.log('[Swatch] Dispatching event with mediaId:', mediaId);
      this.dispatchEvent(new CustomEvent('swatch:change', {
        bubbles: true,
        detail: {
          mediaId: mediaId,
          variantImage: variantImage
        }
      }));
    } else {
      console.warn('[Swatch] No mediaId found on button');
    }

    // Update active state efficiently using data attribute
    const swatchButtons = this.querySelectorAll('[data-swatch-button]');
    swatchButtons.forEach((btn) => {
      btn.removeAttribute('data-selected');
    });
    
    // Set selected on clicked button
    button.setAttribute('data-selected', 'true');

    // Instant image switch (no setTimeout, using CSS transitions)
  }

  /**
   * Updates ALL product images instantly (already cached from hover)
   */
  #updateImage(src, srcset, alt) {
    if (this.productImages.length === 0) return;

    // Direct update - no setTimeout (CSS handles transition via opacity class)
    this.productImages.forEach((img) => {
      img.src = src;
      if (srcset) {
        img.srcset = srcset;
      }
      img.alt = alt || this.originalAlt || '';
    });
  }

  /**
   * Set up "Show all" button functionality
   * Reveals hidden swatches when clicked
   */
  #setupShowAllButton() {
    const showAllButton = this.querySelector('[data-show-all-button]');
    if (!showAllButton) return;

    const action = showAllButton.dataset.action;

    // If action is "url", it's a link - no JS needed
    if (action === 'url') return;

    // If action is "expand", reveal hidden swatches
    if (action === 'expand') {
      showAllButton.addEventListener('click', (e) => {
        // Prevent click from bubbling to parent product card link
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Find all hidden swatches within THIS component instance
        const hiddenSwatches = this.querySelectorAll('[data-hidden-swatch]');
        
        // Reveal them with a simple class toggle
        hiddenSwatches.forEach(swatch => {
          swatch.classList.remove('hidden');
          swatch.removeAttribute('data-hidden-swatch');
        });

        // Remove the button after expanding
        showAllButton.remove();
        
        return false;
      }, true); // Use capture phase to prevent parent handlers
    }
  }
}

// Register component
if (!customElements.get('swatches-item-component')) {
  customElements.define('swatches-item-component', SwatchesItemComponent);
}

